import React, { useRef, useCallback, useEffect, useState } from 'react';

import { Box, createStyles, makeStyles, Theme, IconButton } from '@material-ui/core';
import { MdFormatBold, MdFormatItalic } from 'react-icons/md';

import { getBackground } from '../../theme/styles';
import { markdownToHtml, htmlToMarkdown } from '../../utils/simpleMarkdown';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      border: `1px solid ${getBackground(theme.palette.type === 'dark').backgroundInput}`,
      borderRadius: theme.shape.borderRadius,
      backgroundColor: getBackground(theme.palette.type === 'dark').backgroundInput,
      transition: theme.transitions.create(['border-color', 'box-shadow']),
      '&:hover': {
        backgroundColor: theme.palette.background.paper,
        borderColor: theme.palette.primary.main,
      },
      '&:focus-within': {
        backgroundColor: theme.palette.background.paper,
        borderColor: theme.palette.primary.main,
      },
    },
    toolbar: {
      display: 'flex',
      alignItems: 'center',
      padding: '4px 8px',
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    toolbarButtonActive: {
      backgroundColor: theme.palette.action.selected,
    },
    editableWrapper: {
      resize: 'vertical',
      overflow: 'auto',
      minHeight: 80,
      maxHeight: 400,
    },
    editable: {
      padding: '10px 24px 10px',
      fontSize: 16,
      lineHeight: 1.5,
      minHeight: '100%',
      outline: 'none',
      '& strong': {
        fontWeight: 700,
      },
      '& em, & i': {
        fontStyle: 'italic',
      },
      '& br': {
        display: 'block',
        content: '""',
        marginTop: 4,
      },
      '&[data-placeholder]:empty::before': {
        content: 'attr(data-placeholder)',
        color: theme.palette.text.hint,
      },
    },
  })
);

interface Props {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  rows?: number;
  fullWidth?: boolean;
}

/**
 * WYSIWYG rich text editor. Displays formatted text (bold) instead of **.
 * Supports: new lines (Enter), bold and italic via toolbar. Stores value as Markdown.
 */
export const SimpleRichTextEditor: React.FC<Props> = ({
  value,
  onChange,
  label,
  placeholder = '',
  rows = 3,
  fullWidth,
}) => {
  const classes = useStyles();
  const editableRef = useRef<HTMLDivElement>(null);
  const isInternalChange = useRef(false);
  const [boldActive, setBoldActive] = useState(false);
  const [italicActive, setItalicActive] = useState(false);

  const syncToolbarState = useCallback(() => {
    const el = editableRef.current;
    if (!el || document.activeElement !== el) {
      setBoldActive(false);
      setItalicActive(false);
      return;
    }
    try {
      setBoldActive(document.queryCommandState('bold'));
      setItalicActive(document.queryCommandState('italic'));
    } catch {
      setBoldActive(false);
      setItalicActive(false);
    }
  }, []);

  useEffect(() => {
    const onSelectionChange = () => {
      const el = editableRef.current;
      if (!el || document.activeElement !== el) return;
      syncToolbarState();
    };
    document.addEventListener('selectionchange', onSelectionChange);
    return () => document.removeEventListener('selectionchange', onSelectionChange);
  }, [syncToolbarState]);

  // Sync external value to contenteditable
  useEffect(() => {
    if (isInternalChange.current) {
      isInternalChange.current = false;
      return;
    }
    const el = editableRef.current;
    if (!el) return;

    const html = value ? markdownToHtml(value) : '';
    if (el.innerHTML !== html) {
      el.innerHTML = html || '';
    }
  }, [value]);

  const handleInput = useCallback(() => {
    const el = editableRef.current;
    if (!el) return;

    isInternalChange.current = true;
    const markdown = htmlToMarkdown(el.innerHTML);
    onChange(markdown);
    syncToolbarState();
  }, [onChange, syncToolbarState]);

  const handleFocus = useCallback(() => {
    syncToolbarState();
  }, [syncToolbarState]);

  const handleBlur = useCallback(() => {
    handleInput();
    setBoldActive(false);
    setItalicActive(false);
  }, [handleInput]);

  const handleBoldClick = useCallback(() => {
    const el = editableRef.current;
    if (!el) return;
    el.focus();
    // Emit semantic <b>/<i> tags instead of CSS-styled spans so formatting survives
    // the HTML↔markdown round-trip.
    try {
      document.execCommand('styleWithCSS', false, 'false');
    } catch {}
    document.execCommand('bold', false);
    requestAnimationFrame(() => handleInput());
  }, [handleInput]);

  const handleItalicClick = useCallback(() => {
    const el = editableRef.current;
    if (!el) return;
    el.focus();
    try {
      document.execCommand('styleWithCSS', false, 'false');
    } catch {}
    document.execCommand('italic', false);
    requestAnimationFrame(() => handleInput());
  }, [handleInput]);

  const minHeight = Math.max(80, (rows || 3) * 24);

  return (
    <Box className={classes.root} width={fullWidth ? '100%' : undefined}>
      <Box className={classes.toolbar}>
        <IconButton
          size="small"
          onClick={handleBoldClick}
          title="Bold"
          aria-label="Bold"
          aria-pressed={boldActive}
          color={boldActive ? 'primary' : 'default'}
          className={boldActive ? classes.toolbarButtonActive : undefined}
        >
          <MdFormatBold size={20} />
        </IconButton>
        <IconButton
          size="small"
          onClick={handleItalicClick}
          title="Italic"
          aria-label="Italic"
          aria-pressed={italicActive}
          color={italicActive ? 'primary' : 'default'}
          className={italicActive ? classes.toolbarButtonActive : undefined}
        >
          <MdFormatItalic size={20} />
        </IconButton>
      </Box>
      <div className={classes.editableWrapper} style={{ minHeight }}>
        <div
          ref={editableRef}
          className={classes.editable}
          contentEditable
          data-placeholder={placeholder}
          onInput={handleInput}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onMouseUp={syncToolbarState}
          onKeyUp={syncToolbarState}
          suppressContentEditableWarning
          role="textbox"
          aria-label={label}
        />
      </div>
    </Box>
  );
};
